import React, { useLayoutEffect, useState } from 'react';
import { toolsUrl } from './sso';

// Kept identical in frontend, wallet and cooking so each app can build independently.
export const THEME_KEY = 'breck:theme';
export const THEME_COOKIE = 'breck_theme';
export const accountThemeKey = account => account?.id != null ? `${THEME_KEY}:user:${account.id}`
  : account?.username ? `${THEME_KEY}:user:${account.username.toLowerCase()}` : null;
const accountCookie = key => `${THEME_COOKIE}_user_${encodeURIComponent(key.slice(`${THEME_KEY}:user:`.length))}`;
const isTheme = value => value === 'dark' || value === 'light';
function read(key) {
  try { const value = localStorage.getItem(key); return isTheme(value) ? value : null; }
  catch { return null; }
}
function write(key, value) {
  try { localStorage.setItem(key, value); } catch { /* Appearance still works when storage is blocked. */ }
}
function readCookie(name) {
  try {
    const value = document.cookie.split(';').map(part => part.trim()).find(part => part.startsWith(`${name}=`))?.slice(name.length + 1);
    return isTheme(value) ? value : null;
  } catch { return null; }
}
function writeCookie(name, theme) {
  const host = window.location.hostname;
  const domain = host === 'app-breck.com' || host.endsWith('.app-breck.com') ? '; Domain=app-breck.com' : '';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  try { document.cookie = `${name}=${theme}; Path=/; Max-Age=31536000; SameSite=Lax${domain}${secure}`; }
  catch { /* Keep a local fallback if cookies are unavailable. */ }
}
const browserTheme = () => readCookie(THEME_COOKIE) || read(THEME_KEY);
function remember(theme, key) {
  writeCookie(THEME_COOKIE, theme); write(THEME_KEY, theme);
  if (key) { writeCookie(accountCookie(key), theme); write(key, theme); }
}
function apply(theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.content = theme === 'dark' ? '#17191d' : '#f7f8f6';
  if (window.parent !== window) window.parent.postMessage({ type: 'breck:theme', theme }, toolsUrl());
}

// undefined means the signed-in profile is still loading; null means signed out.
export default function ThemeToggle({ account }) {
  const key = accountThemeKey(account);
  const pending = account === undefined;
  const [theme, setTheme] = useState(() => browserTheme() || 'light');
  useLayoutEffect(() => {
    const next = (!pending && key && (readCookie(accountCookie(key)) || read(key))) || browserTheme()
      || (isTheme(document.documentElement.dataset.theme) ? document.documentElement.dataset.theme : 'light');
    setTheme(next); apply(next);
    if (!pending) remember(next, key);
    function sync(event) {
      if (event?.type === 'storage' && event.key !== null && event.key !== THEME_KEY && event.key !== key) return;
      // The global cookie is authoritative across app origins, even if this tab's
      // local backup or signed-in account differs from the app that changed it.
      const saved = browserTheme() || 'light';
      setTheme(saved);
      if (document.documentElement.dataset.theme !== saved) apply(saved);
      if (read(THEME_KEY) !== saved) write(THEME_KEY, saved);
    }
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    // Storage events and BroadcastChannel do not cross subdomains or ports.
    const timer = window.setInterval(sync, 1000);
    return () => {
      window.removeEventListener('storage', sync); window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync); window.clearInterval(timer);
    };
  }, [key, pending]);

  function toggle() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next); apply(next); remember(next, key);
  }
  return <button type="button" className="theme-toggle" aria-label="Dark mode" aria-pressed={theme === 'dark'}
    title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} onClick={toggle}>
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      {theme === 'dark' ? <><circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5"/></>
        : <path d="M20.5 13A8.5 8.5 0 0 1 11 3.5 8.5 8.5 0 1 0 20.5 13Z"/>}
    </svg><span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
  </button>;
}
